/**
 * Seeded Simplex/Perlin noise engine for generative art.
 * Ported from generative-backgrounds-v3.jsx reference.
 *
 * Usage:
 *   const engine = createNoiseEngine(seed);
 *   engine.noise2(x, y);      // 2D noise [-1, 1]
 *   engine.fbm(x, y, oct);    // Fractal Brownian Motion
 *   engine.ridged(x, y, oct); // Ridged multifractal
 *   engine.rng();             // Seeded random [0, 1)
 */

const GRAD = [[1,1],[-1,1],[1,-1],[-1,-1],[1,0],[-1,0],[0,1],[0,-1]];

function fade(t) { return t * t * t * (t * (t * 6 - 15) + 10); }
function lerp(a, b, t) { return a + t * (b - a); }

export function createNoiseEngine(seed) {
  // ── Seeded RNG (LCG) ──
  let _rng = Math.abs(seed | 0) || 1;
  function rng() {
    _rng = (_rng * 16807 + 0) % 2147483647;
    return (_rng - 1) / 2147483646;
  }

  // ── Build permutation table ──
  const PERM = new Uint8Array(512);
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [p[i], p[j]] = [p[j], p[i]];
  }
  for (let i = 0; i < 512; i++) PERM[i] = p[i & 255];

  // ── 2D Perlin noise ──
  function noise2(x, y) {
    const X = Math.floor(x) & 255, Y = Math.floor(y) & 255;
    const xf = x - Math.floor(x), yf = y - Math.floor(y);
    const u = fade(xf), v = fade(yf);
    const g = (h, dx, dy) => {
      const g2 = GRAD[PERM[h] & 7];
      return g2[0] * dx + g2[1] * dy;
    };
    const aa = PERM[PERM[X] + Y], ab = PERM[PERM[X] + Y + 1];
    const ba = PERM[PERM[X + 1] + Y], bb = PERM[PERM[X + 1] + Y + 1];
    return lerp(
      lerp(g(aa, xf, yf), g(ba, xf - 1, yf), u),
      lerp(g(ab, xf, yf - 1), g(bb, xf - 1, yf - 1), u),
      v
    );
  }

  // ── Fractal Brownian Motion ──
  function fbm(x, y, oct = 4, lac = 2.0, gain = 0.5) {
    let val = 0, amp = 1, freq = 1, max = 0;
    for (let i = 0; i < oct; i++) {
      val += noise2(x * freq, y * freq) * amp;
      max += amp;
      amp *= gain;
      freq *= lac;
    }
    return val / max;
  }

  // ── Ridged multifractal ──
  function ridged(x, y, oct = 4) {
    let val = 0, amp = 1, freq = 1, max = 0;
    for (let i = 0; i < oct; i++) {
      let n = 1 - Math.abs(noise2(x * freq, y * freq));
      n *= n;
      val += n * amp;
      max += amp;
      amp *= 0.5;
      freq *= 2.1;
    }
    return val / max;
  }

  return { noise2, fbm, ridged, rng, lerp };
}

// Re-export lerp for use in renderers
export { lerp };
